import { useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MapPin, Phone, Mail, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    message: ''
  });
  const [feedbackData, setFeedbackData] = useState({
    name: '',
    message: '',
    rating: 5
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  const handleContactChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFeedbackChange = (e) => {
    setFeedbackData({
      ...feedbackData,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ first_name: '', last_name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    setSubmittingFeedback(true);
    try {
      await axios.post(`${API}/testimonials`, feedbackData);
      toast.success('Thank you for your feedback!');
      setFeedbackData({ name: '', message: '', rating: 5 });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs md:text-sm uppercase tracking-wider text-[#14B8A6] mb-2 md:mb-3 font-semibold" data-testid="contact-label">
              Get In Touch
            </p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-gray-900 mb-3 md:mb-4 px-2" data-testid="contact-heading">
              Contact Us
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2" data-testid="contact-description">
              We will respond to you as soon as we can.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8" data-testid="contact-info-section">
              <div className="bg-white border border-gray-200 p-8 rounded-xl shadow-sm">
                <h2 className="text-2xl font-heading font-semibold text-gray-900 mb-6" data-testid="info-heading">
                  Store Information
                </h2>
                <div className="space-y-6">
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Corbin+KY" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-start hover:text-[#14B8A6] transition-colors group" 
                    data-testid="address-info"
                  >
                    <MapPin className="w-6 h-6 text-[#14B8A6] mr-4 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                      <p className="text-gray-600 group-hover:text-[#14B8A6]">
                        Corbin, KY
                      </p>
                    </div>
                  </a>
                  <a 
                    href="tel:+15025550123"
                    className="flex items-start hover:text-[#14B8A6] transition-colors group"
                    data-testid="phone-info"
                  >
                    <Phone className="w-6 h-6 text-[#14B8A6] mr-4 mt-1 group-hover:scale-110 transition-transform" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                      <p className="text-gray-600 group-hover:text-[#14B8A6]">(502) 555-0123</p>
                    </div>
                  </a>
                  <a 
                    href="mailto:info@familyshoeshop.com"
                    className="flex items-start hover:text-[#14B8A6] transition-colors group" 
                    data-testid="email-info"
                  >
                    <Mail className="w-6 h-6 text-[#14B8A6] mr-4 mt-1 group-hover:scale-110 transition-transform" />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <p className="text-gray-600 group-hover:text-[#14B8A6]">info@familyshoeshop.com</p>
                    </div>
                  </a>
                  <div className="flex items-start" data-testid="hours-info">
                    <svg className="w-6 h-6 text-[#14B8A6] mr-4 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Business Hours</h3>
                      <div className="text-gray-600 space-y-1">
                        <p>Mon-Fri: 8:30 AM - 8:30 PM</p>
                        <p>Saturday: 12:00 PM - 5:00 PM</p>
                        <p>Sunday: 1:00 PM - 5:00 PM</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <form onSubmit={handleContactSubmit} className="bg-white border border-[#E5E4DE] p-8" data-testid="contact-form">
                <h2 className="text-2xl font-heading font-semibold text-[#1F2937] mb-6" data-testid="form-heading">
                  Send Us a Message
                </h2>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name" data-testid="first-name-label">First Name</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleContactChange}
                        required
                        data-testid="first-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name" data-testid="last-name-label">Last Name</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleContactChange}
                        required
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" data-testid="email-label">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleContactChange}
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" data-testid="message-label">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleContactChange}
                      rows={5}
                      required
                      data-testid="message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full flex items-center justify-center"
                    data-testid="submit-contact-button"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </form>

              <form onSubmit={handleFeedbackSubmit} className="bg-[#F5F5F0] p-8" data-testid="feedback-form">
                <div className="flex items-center mb-6">
                  <MessageCircle className="w-6 h-6 text-[#3E2723] mr-3" />
                  <h2 className="text-2xl font-heading font-semibold text-[#1F2937]" data-testid="feedback-heading">
                    Share Your Feedback
                  </h2>
                </div>
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="feedback_name" data-testid="feedback-name-label">Your Name</Label>
                    <Input
                      id="feedback_name"
                      name="name"
                      value={feedbackData.name}
                      onChange={handleFeedbackChange}
                      required
                      data-testid="feedback-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="feedback_message" data-testid="feedback-message-label">Your Feedback</Label>
                    <Textarea
                      id="feedback_message"
                      name="message"
                      value={feedbackData.message}
                      onChange={handleFeedbackChange}
                      rows={4}
                      required
                      data-testid="feedback-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submittingFeedback}
                    className="btn-primary w-full"
                    data-testid="submit-feedback-button"
                  >
                    {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#3E2723] text-white" data-testid="thank-you-section">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold mb-6" data-testid="thank-you-heading">
            Thank You for Coming!
          </h2>
          <p className="text-xl text-white/90" data-testid="thank-you-message">
            We look forward to serving you and your family. Come again!
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;